import { useApolloClient, useLazyQuery } from "@apollo/client";
import { useCallback, useState } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import { Download } from "components/icons/generated";
import Button from "ds/components/Button";
import Tooltip from "ds/components/Tooltip";
import useAnalytics from "hooks/useAnalytics";
import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";
import { fetchSanitizedLogs } from "shared/Run/fetchSanitizedLogs";
import { Module, RunState, Stack } from "types/generated";
import { downloadFile, downloadFileFromURL } from "utils/file";
import { RunContext } from "components/Run/Context";

import {
  GET_MODULE_LOGS_DOWNLOAD_URL,
  GET_MODULE_LOGS_SANITIZED,
  GET_STACK_LOGS_DOWNLOAD_URL,
  GET_STACK_LOGS_SANITIZED,
} from "./gql";

const getLogsUnavailableMessage = (logs?: { expired: boolean; ongoing: boolean } | null) => {
  if (logs?.expired) {
    return "Logs have expired and are no longer available for download.";
  }

  if (logs?.ongoing) {
    return "Logs will be available for download once the run completes.";
  }

  return "Logs are not yet available for download. Please try again later.";
};

type DownloadLogsButtonProps = {
  expired: boolean;
  state: RunState;
  stateVersion?: number | null;
  runId: string;
  stackId: string;
  isModuleRun: boolean;
};

const DownloadLogsButton = ({
  state,
  stateVersion,
  expired,
  runId,
  stackId,
  isModuleRun,
}: DownloadLogsButtonProps) => {
  const { reportError } = useTypedContext(FlashContext);
  const {
    run: { state: runState },
  } = useTypedContext(RunContext);
  const trackSegmentAnalyticsEvent = useAnalytics();
  const { stripAnsiCodesFromLogs } = useTypedFlags();
  const client = useApolloClient();
  const [loading, setLoading] = useState(false);

  const [getDownloadUrl] = useLazyQuery<{ module: Module } | { stack: Stack }>(
    isModuleRun ? GET_MODULE_LOGS_DOWNLOAD_URL : GET_STACK_LOGS_DOWNLOAD_URL
  );

  const handleDownloadCLick = useCallback(async () => {
    const fileName = `${stackId}-${runId}-${state}.logs.txt`;

    setLoading(true);

    try {
      if (stripAnsiCodesFromLogs) {
        const logs = await fetchSanitizedLogs(
          client,
          isModuleRun ? GET_MODULE_LOGS_SANITIZED : GET_STACK_LOGS_SANITIZED,
          {
            [isModuleRun ? "moduleId" : "stackId"]: stackId,
            runId,
            state,
            stateVersion,
          }
        );

        if (!logs.exists) {
          reportError({ message: getLogsUnavailableMessage(logs) });
          return;
        }

        downloadFile(fileName, logs.text);
      } else {
        const { data } = await getDownloadUrl({
          variables: {
            [isModuleRun ? "moduleId" : "stackId"]: stackId,
            runId,
            state,
            stateVersion,
          },
        });

        const entity = data && ("module" in data ? data.module : data.stack);
        const logsDownloadURL = entity?.run?.logsDownloadURL;

        if (!logsDownloadURL?.url) {
          reportError({ message: getLogsUnavailableMessage(logsDownloadURL) });
          return;
        }

        downloadFileFromURL(logsDownloadURL.url, fileName);
      }

      trackSegmentAnalyticsEvent("Run Logs - download logs", {
        logsState: state,
        runState,
      });
    } catch {
      reportError({
        title: "Download failed",
        message: "Failed to download logs. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  }, [
    client,
    getDownloadUrl,
    runId,
    state,
    reportError,
    trackSegmentAnalyticsEvent,
    runState,
    stripAnsiCodesFromLogs,
    stackId,
    isModuleRun,
    stateVersion,
  ]);

  return (
    <Tooltip
      active={expired}
      on={(props) => (
        <Button
          {...props}
          variant="secondary"
          startIcon={Download}
          size="small"
          onPress={handleDownloadCLick}
          loading={loading}
          disabled={loading || expired}
        >
          Download logs
        </Button>
      )}
    >
      These logs have expired.
    </Tooltip>
  );
};

export default DownloadLogsButton;
